import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { identifyUser, resetAnalytics } from "@/lib/analytics";

type Profile = Tables<"users">;

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  signOut: async () => {},
  refreshProfile: async () => {},
});

export const useAuth = () => useContext(AuthContext);

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (userId: string, retries = 3): Promise<Profile | null> => {
    for (let i = 0; i < retries; i++) {
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("id", userId)
        .single();

      if (data) {
        setProfile(data);
        identifyUser({ id: data.id, email: data.email, plan_type: data.plan_type, state: data.state });
        return data;
      }

      // If error is not "row not found", don't retry
      if (error && error.code !== "PGRST116") {
        console.error("Profile fetch error:", error.message);
        break;
      }

      // Profile not yet created by trigger — wait and retry
      if (i < retries - 1) {
        await delay(500);
      }
    }
    setProfile(null);
    return null;
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        const currentUser = session?.user ?? null;
        setUser(currentUser);

        if (currentUser) {
          // Defer to avoid Supabase client deadlock
          setTimeout(() => fetchProfile(currentUser.id), 0);

          // Update last_active on any session event with a user
          if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "INITIAL_SESSION") {
            setTimeout(() => {
              supabase.rpc("update_last_active").then();
            }, 0);
          }
        } else {
          setProfile(null);
          resetAnalytics();
        }

        setLoading(false);
      }
    );

    // No separate getSession call — onAuthStateChange fires INITIAL_SESSION on mount

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const refreshProfile = useCallback(async () => {
    if (user) {
      await fetchProfile(user.id, 1);
    }
  }, [user, fetchProfile]);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.error("Sign out error:", err);
    } finally {
      // Always clear local state
      setUser(null);
      setProfile(null);
      resetAnalytics();
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, profile, loading, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};
